// SideDrawer — el cajón lateral del mock (`.drawer`): 420 px que entran desde la derecha sobre un
// velo al 40 %, con encabezado, cuerpo que scrollea solo y pie de acciones a la derecha.
//
// Es un diálogo modal de verdad: atrapa el foco, cierra con `Esc` y se anuncia con `aria-modal`.
// El contenido lo pone quien lo usa; el patrón solo pone el marco.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Qué elementos del cajón puede alcanzar el tabulador. */
const FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface SideDrawerProps {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    children: ReactNode;
    /** Los botones del pie. Sin pie —como los cajones que son solo una lista de opciones— se omite. */
    footer?: ReactNode;
    /** `bottom` sube desde abajo casi a pantalla completa, para teléfonos. */
    placement?: 'right' | 'bottom';
    className?: string;
}

const PLACEMENT_CLASS: Record<NonNullable<SideDrawerProps['placement']>, string> = {
    right: 'inset-y-0 right-0 w-[420px] max-w-[92vw] border-l shadow-[-8px_0_24px_rgba(0,0,0,0.10)]',
    bottom: 'inset-x-0 bottom-0 h-[92dvh] rounded-t-2xl border-t shadow-[0_-8px_24px_rgba(0,0,0,0.10)]',
};

export function SideDrawer({
    open,
    onClose,
    title,
    children,
    footer,
    placement = 'right',
    className,
}: SideDrawerProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const titleId = useId();

    // Quién abrió el cajón, para devolverle el foco al cerrar.
    useEffect(() => {
        if (!open) return;
        openerRef.current = document.activeElement as HTMLElement | null;
        return () => {
            const opener = openerRef.current;
            if (opener?.isConnected === true) opener.focus();
        };
    }, [open]);

    // El foco entra al cajón en cuanto abre: si no, el tabulador sigue recorriendo la pantalla de atrás.
    useEffect(() => {
        if (!open) return;
        const panel = panelRef.current;
        if (panel === null) return;
        const first = panel.querySelector<HTMLElement>(FOCUSABLE);
        (first ?? panel).focus();
    }, [open]);

    // `Esc` cierra y el tabulador da la vuelta adentro. Se escucha en el documento y no en el
    // panel: así el foco no se escapa ni cuando el usuario llegó al cajón por fuera del tabulador.
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // Con un desplegable abierto el foco está fuera del panel: ese `Esc` es del desplegable.
                const active = document.activeElement;
                if (
                    active !== null &&
                    active !== document.body &&
                    !panelRef.current?.contains(active)
                )
                    return;
                // Un combobox con la lista abierta usa ese `Esc` para cerrarla.
                if (active?.getAttribute('aria-expanded') === 'true') return;
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key !== 'Tab') return;
            const panel = panelRef.current;
            if (panel === null) return;
            const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
            if (items.length === 0) return;
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            if (event.shiftKey && (active === first || active === panel)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <>
            {/* El velo cierra al hacer clic. Va fuera del árbol accesible: cerrar ya se ofrece
                por el botón y por `Esc`, y un segundo control diría lo mismo dos veces. */}
            <button
                type="button"
                aria-hidden
                tabIndex={-1}
                onClick={onClose}
                className="fixed inset-0 z-30 bg-black/40"
                data-testid="side-drawer-overlay"
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={cn(
                    'fixed z-40 flex flex-col border-border bg-card outline-none',
                    PLACEMENT_CLASS[placement],
                    className,
                )}
            >
                <div className="flex flex-none items-center justify-between border-b border-border px-4 py-3">
                    <div id={titleId} className="text-[13px] font-semibold">
                        {title}
                    </div>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        onClick={onClose}
                        className="rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">{children}</div>
                {footer === undefined ? null : (
                    <div className="flex flex-none justify-end gap-1.5 border-t border-border bg-muted/30 px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))]">
                        {footer}
                    </div>
                )}
            </div>
        </>
    );
}
